import { compareDesc, format } from 'date-fns';

import { Validator } from './types';
/**
 * An (almost) perect Email regex™!
 *
 * @see https://emailregex.com/
 */
// I trust this crowdsourced regex more than eslint
// eslint-disable-next-line no-useless-escape
export const EmailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * A URL regex!
 *
 * @see https://urlregex.com/
 */
// I trust this crowdsourced regex more than eslint
// eslint-disable-next-line no-useless-escape
export const URLRegex =
  /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/;

export const PhoneRegex = /^\+?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

const PostalCodeRegex = /^[0-9]{5}?$/;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const returnIfEmpty = (callback: Validator) => (value: any) => {
  if (value === null) return null;

  return callback(value);
};

export const customizeValidatorMessage =
  <T>(validator: (v: T) => null | string, message: string) =>
  (value: T) => {
    const result = validator(value);

    return result !== null ? message : null;
  };

// Numbers

export const required = (v: unknown) =>
  v !== '' && v !== null && v !== undefined ? null : 'This field is required';

export const subtleRequired = (message: string) => (v: unknown) =>
  // @ts-expect-error A valid value will have a length, an invalid one will not
  v !== '' && v?.length && v !== null && v !== undefined ? null : message;

export const min = (minValue: number, inclusive = true) =>
  returnIfEmpty((n: number) =>
    (inclusive ? n >= minValue : n > minValue)
      ? null
      : `Must be at least ${minValue}`,
  );

export const max = (maxValue: number, inclusive = true) =>
  returnIfEmpty((n: number) =>
    (inclusive ? n <= maxValue : n < maxValue)
      ? null
      : `Must be less than ${maxValue}`,
  );

export const range = (min: number, max: number, inclusive = true) =>
  returnIfEmpty((n: number) =>
    (inclusive ? n <= max && n >= min : n < max && n > min)
      ? null
      : `Must be between ${min} and ${max}`,
  );

// Strings

export const matches = (
  regex: RegExp,
  message = 'Must be properly formatted',
) => returnIfEmpty((v: string) => (v.match(regex) ? null : message));

export const isEmail = matches(
  EmailRegex,
  'There was an error submitting your request. Please try again later.',
);

export const isEmailFormatted = matches(
  EmailRegex,
  'Please check that the email address is correct',
);

export const isPostalCode = matches(
  PostalCodeRegex,
  'ZIP code should be in the 5 number format',
);

export const strLenMin = (charCount: number) =>
  returnIfEmpty((v: string) => {
    const testString = v.trim().replace(/[\r\n]/g, '\n');
    // string[Symbol.Iterator] returns code points, so this is more accurate
    // for strings with emoji. If this is not desired for a particular endpoint,
    // we'll need to either polyfill Intl.Segmenter or grab a Grapheme library
    // .....or just use string.length if the endpoint is expecting a count of
    //      UTF-16 units
    return [...testString].length >= charCount
      ? null
      : `Must be at least ${charCount} characters`;
  });

export const strLenMax = (charCount: number) =>
  returnIfEmpty((v: string) => {
    const testString = v.trim().replace(/[\r\n]/g, '\n');
    // string[Symbol.Iterator] returns code points, so this is more accurate
    // for strings with emoji. If this is not desired for a particular endpoint,
    // we'll need to either polyfill Intl.Segmenter or grab a Grapheme library
    // .....or just use string.length if the endpoint is expecting a count of
    //      UTF-16 units
    return [...testString].length <= charCount
      ? null
      : `Must not be longer than ${charCount} characters`;
  });

const unstrippedIsPhoneNumber = matches(
  PhoneRegex,
  'Phone number must be 10 digits (No country code)',
);
export const isPhoneNumber = (v: string) =>
  unstrippedIsPhoneNumber(v.replace(/[^\d]/g, ''));

export const isPhoneNumberOrEmpty = (v: string) => {
  if (!v) {
    return null;
  }
  return unstrippedIsPhoneNumber(v.replace(/[^\d]/g, ''));
};

// Dates

export const before = (testDate: Date, msgFormat = 'yyyy-MM-dd') =>
  returnIfEmpty((v: Date) =>
    compareDesc(v, testDate) === 1
      ? null
      : `Must be before ${format(testDate, msgFormat)}`,
  );

export const after = (testDate: Date, msgFormat = 'yyyy-MM-dd') =>
  returnIfEmpty((v: Date) =>
    compareDesc(v, testDate) === -1
      ? null
      : `Must be after ${format(testDate, msgFormat)}`,
  );

export const inDateRange = (
  minDate: Date,
  maxDate: Date,
  msgFormat = 'yyyy-MM-dd',
) =>
  returnIfEmpty((v: Date) =>
    compareDesc(v, maxDate) === 1 && compareDesc(v, minDate) === -1
      ? null
      : `Must be between ${format(minDate, msgFormat)} and ${format(
          maxDate,
          msgFormat,
        )}`,
  );

export const maxLenght = (
  lenght: number,
  inclusive: boolean,
  customMessage?: string,
) =>
  returnIfEmpty((n: string) =>
    (inclusive ? n.length <= lenght : n.length < lenght)
      ? null
      : customMessage
      ? customMessage
      : `Must be less than ${lenght}`,
  );

export const minLenght = (lenght: number, inclusive = true) =>
  returnIfEmpty((n: string) =>
    (inclusive ? n.length >= lenght : n.length > lenght)
      ? null
      : `Must be at least ${lenght}`,
  );
